import { getStoredValue, storeValue } from '../../utils/localStorage';
import type { DocumentAuditAction, DocumentDownloadLogFilters } from '../../api/downloadLogs';

const VALID_ACTIONS: DocumentAuditAction[] = ['download', 'created', 'updated', 'deleted'];

const DEFAULT_FILTERS: DocumentDownloadLogFilters = {};

const storageKey = (scope: 'project' | 'global', projectId?: string): string =>
  scope === 'project' ? `downloadLogsFilters:project:${projectId}` : 'downloadLogsFilters:global';

/**
 * Per-field type check — a plain-object check alone would accept a corrupted
 * value like `{ file_size_min: "abc" }`, which could produce a NaN query
 * param or a broken number input downstream. Every known field is optional,
 * so `undefined` is always valid for it; when present, it must be the right
 * primitive type.
 */
const isValidFilters = (value: unknown): value is DocumentDownloadLogFilters => {
  if (typeof value !== 'object' || value === null || Array.isArray(value)) return false;
  const v = value as Record<string, unknown>;

  const isOptionalString = (x: unknown) => x === undefined || typeof x === 'string';
  const isOptionalNumber = (x: unknown) => x === undefined || typeof x === 'number';

  const isOptionalAction = (x: unknown) => x === undefined || (typeof x === 'string' && VALID_ACTIONS.includes(x as DocumentAuditAction));

  return (
    isOptionalString(v.user_id) &&
    isOptionalString(v.document_id) &&
    isOptionalAction(v.action) &&
    isOptionalString(v.document_name) &&
    isOptionalString(v.content_type) &&
    isOptionalNumber(v.file_size_min) &&
    isOptionalNumber(v.file_size_max) &&
    isOptionalString(v.date_from) &&
    isOptionalString(v.date_to)
  );
};

/** Reads the last-used filters for this scope (and project, if project-scoped); falls back to {} if unset, invalid, or unavailable. */
export const getStoredFilters = (scope: 'project' | 'global', projectId?: string): DocumentDownloadLogFilters =>
  getStoredValue(storageKey(scope, projectId), isValidFilters, DEFAULT_FILTERS);

export const storeFilters = (
  scope: 'project' | 'global',
  projectId: string | undefined,
  filters: DocumentDownloadLogFilters,
): void => storeValue(storageKey(scope, projectId), filters);
